import { LINK_QTY_MAX, LINK_QTY_MIN } from "@giftforward/naming";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";
import type { RecipientAdditionsQuote } from "@/interfaces/campaignContacts";
import type {
  AddGiftLinksBody,
  AddGiftLinksResult,
  GiftLinkAdditionsQuote,
} from "@/interfaces/giftLinks";
import { clampLinkQty, commitLinkQtyText, stepLinkQty } from "@/utils/link-quantity-helper";
import { canContinuePayment } from "../contacts/cardPurchase-helper";
import type { WizardPurchase } from "../contacts/types";
import type { GiftLinksWizardAction, GiftLinksWizardState, GiftLinksWizardStep } from "./types";

const COPY = GIFT_LINKS_COPY.wizard;
const MONEY_REFUSAL_STATUS = 422;
const DIGITS_ONLY = /^\d+$/;

export function initialGiftLinksWizardState(): GiftLinksWizardState {
  return {
    step: "quantity",
    qty: LINK_QTY_MIN,
    qtyText: String(LINK_QTY_MIN),
    notice: null,
    busy: false,
    purchase: null,
    depositPreference: "card",
  };
}

/** Typing is free: the committed qty follows only a text that is a whole number inside the range. */
function typedQty(state: GiftLinksWizardState, text: string): GiftLinksWizardState {
  const trimmed = text.trim();
  const n = DIGITS_ONLY.test(trimmed) ? Number(trimmed) : Number.NaN;
  const valid = n >= LINK_QTY_MIN && n <= LINK_QTY_MAX;
  return { ...state, qtyText: text, qty: valid ? clampLinkQty(n) : state.qty };
}

function committedQty(state: GiftLinksWizardState, qty: number): GiftLinksWizardState {
  return { ...state, qty, qtyText: String(qty) };
}

/** A refused write: a money refusal (422) returns to payment, anything else keeps the step. */
function failedState(
  state: GiftLinksWizardState,
  status: number,
  message: string,
): GiftLinksWizardState {
  const base = { ...state, notice: message, busy: false };
  return status === MONEY_REFUSAL_STATUS ? { ...base, step: "payment" } : base;
}

/** A processing purchase once its status is known: settled keeps its id, failed drops it. */
function resolvedPurchase(
  purchase: WizardPurchase | null,
  outcome: "success" | "failed",
): WizardPurchase | null {
  if (purchase === null || outcome === "failed") return null;
  return { ...purchase, outcome };
}

export function giftLinksWizardReducer(
  state: GiftLinksWizardState,
  action: GiftLinksWizardAction,
): GiftLinksWizardState {
  switch (action.type) {
    case "typeQty":
      return typedQty(state, action.text);
    case "blurQty":
      return committedQty(state, commitLinkQtyText(state.qtyText));
    case "stepQty":
      return committedQty(state, stepLinkQty(state.qtyText, action.delta));
    case "next":
      return {
        ...committedQty(state, commitLinkQtyText(state.qtyText)),
        step: "payment",
        notice: null,
      };
    case "back":
      return { ...state, step: "quantity", notice: null };
    case "purchased":
      return { ...state, purchase: action.purchase };
    case "purchaseResolved":
      return { ...state, purchase: resolvedPurchase(state.purchase, action.outcome) };
    case "pickDepositMode":
      return { ...state, depositPreference: action.mode };
    case "submit":
      return { ...state, busy: true, notice: null };
    case "failed":
      return failedState(state, action.status, action.message);
    case "dismissNotice":
      return { ...state, notice: null };
  }
}

export function giftLinksWizardTitle(
  step: GiftLinksWizardStep,
  qty: number,
  campaignName: string,
  pricePerGift: string,
): { title: string; subtitle: string } {
  if (step === "quantity") {
    return { title: COPY.title, subtitle: COPY.subtitle(campaignName, pricePerGift) };
  }
  return { title: COPY.paymentTitle(qty, campaignName), subtitle: COPY.paymentSub };
}

/** Quantity step: "Continue to payment" when any link is paid, else "Continue"; payment: "Add gift links". */
export function giftLinksPrimaryLabel(step: GiftLinksWizardStep, paid: number): string {
  if (step === "payment") return COPY.actions.add;
  return paid > 0 ? COPY.actions.continueToPayment : COPY.actions.continue;
}

export function giftLinksPrimaryDisabled(i: {
  state: GiftLinksWizardState;
  quote: RecipientAdditionsQuote | undefined;
  fetching: boolean;
}): boolean {
  if (i.state.busy) return true;
  if (i.state.step !== "payment") return false;
  return i.fetching || !canContinuePayment(i.quote, false);
}

/** The add body: the count and the quote's `expected` verbatim (money figures and link count). */
export function addGiftLinksBody(
  state: GiftLinksWizardState,
  quote: GiftLinkAdditionsQuote,
): AddGiftLinksBody {
  const purchaseId = state.purchase === null ? {} : { purchaseId: state.purchase.id };
  return { count: state.qty, expected: quote.expected, ...purchaseId };
}

export function giftLinksAddedToast(result: AddGiftLinksResult, campaignName: string): string {
  return COPY.added(result.contactIds.length, campaignName);
}
